import React, { useEffect, useState } from "react";
import { subscribe, listQueue, flushQueue } from "@/lib/offline";
import { api } from "@/lib/api";
import { Wifi, WifiOff, RefreshCw, CloudUpload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = async () => {
    try { const q = await listQueue(); setPending(q.length); } catch (e) { console.warn("Offline queue count failed", e); }
  };

  useEffect(() => {
    refreshPending();
    const unsub = subscribe(refreshPending);
    const on = () => { setOnline(true); refreshPending(); };
    const off = () => { setOnline(false); refreshPending(); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      unsub();
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const sync = async () => {
    if (!online) { toast.info("Still offline — will retry automatically"); return; }
    setSyncing(true);
    try {
      const r = await flushQueue(api);
      if (r.ok > 0) toast.success(`Synced ${r.ok} change${r.ok === 1 ? "" : "s"}`);
      else if (r.remaining === 0) toast.info("Nothing to sync");
      else toast.warning(`${r.remaining} change(s) still pending`);
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); refreshPending(); }
  };

  const stateColor = !online
    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25"
    : pending > 0
      ? "bg-primary/10 text-primary border-primary/25"
      : "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300 border-emerald-500/25";

  return (
    <button
      type="button"
      onClick={sync}
      data-testid="offline-indicator"
      title={!online ? "Offline — changes are queued locally" : pending > 0 ? "Tap to sync now" : "Online"}
      className={`h-9 rounded-full border px-3 inline-flex items-center gap-2 text-xs font-medium transition-colors ${stateColor}`}
    >
      {syncing ? <Loader2 size={13} className="animate-spin" />
        : !online ? <WifiOff size={13} />
        : pending > 0 ? <CloudUpload size={13} />
        : <Wifi size={13} />}
      <span className="hidden md:inline">
        {syncing ? "Syncing…" : !online ? "Offline" : pending > 0 ? `Sync ${pending}` : "Online"}
      </span>
      {pending > 0 && !syncing && (
        <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">{pending}</span>
      )}
    </button>
  );
}
